import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { getTagsList } from "@/lib/tags";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CheckIcon } from "@radix-ui/react-icons";
import { CompanyDTO } from "@shaarp/common";
import { TagIcon } from "lucide-react";
import { useState } from "react";
import { Button } from "../ui/button";
import { useToastError } from "../ui/use-toast";

type CompanyTagSelectorProps = {
  readonly company: CompanyDTO;
  readonly selectedTag?: string | null;
  readonly onSelectedTagChange?: (tag: string) => void;
  readonly compact?: boolean;
};

export default function CompanyTagSelector({
  company,
  selectedTag: controlledSelectedTag,
  onSelectedTagChange,
  compact = false,
}: CompanyTagSelectorProps) {
  const toastError = useToastError();
  const api = useApi();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [internalSelectedTag, setInternalSelectedTag] = useState<string | null>(
    company?.assignation?.tag || null,
  );

  const selectedTag = controlledSelectedTag ?? internalSelectedTag;

  const tagsList = getTagsList(t);

  const getTag = (value: string) => {
    return tagsList.find((tag) => tag.value === value);
  };

  const getTagLabel = () => {
    if (selectedTag) {
      const tag = getTag(selectedTag);
      return tag ? tag.label : null;
    } else {
      return t("company.tagSelector.notTagged");
    }
  };

  const saveTag = async (newValue: string) => {
    const oldValue = selectedTag;
    setInternalSelectedTag(newValue);
    onSelectedTagChange?.(newValue);
    try {
      await api.put(`/company/${company.id}/assign`, {
        tag: newValue,
      });
    } catch (error) {
      setInternalSelectedTag(oldValue);
      onSelectedTagChange?.(oldValue ?? "");
      toastError();
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "relative h-10 gap-2",
                compact && !selectedTag ? "w-10" : "px-3",
                !!selectedTag && "border-primary text-primary",
              )}
              role="combobox"
              aria-expanded={open}
            >
              <TagIcon className="w-4 h-4 shrink-0" />
              {(!compact || selectedTag) && (
                <span className="text-sm truncate max-w-[120px]">
                  {selectedTag
                    ? getTag(selectedTag)?.label
                    : t("company.tagSelector.notTagged")}
                </span>
              )}
              {!!selectedTag && (
                <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full bg-primary border-2 border-background" />
              )}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">{getTagLabel()}</TooltipContent>
      </Tooltip>
      <PopoverContent className="p-0" side="bottom" align="center">
        <Command>
          <CommandInput placeholder={t("company.tagSelector.search")} />
          <CommandList>
            <CommandEmpty>{t("company.tagSelector.noResults")}</CommandEmpty>
            <CommandGroup>
              {tagsList.map((tag) => (
                <CommandItem
                  key={tag.label}
                  value={tag.label}
                  disabled={false}
                  onSelect={() => {
                    saveTag(tag.value);
                    setOpen(false);
                  }}
                >
                  {tag.label}
                  <CheckIcon
                    className={cn(
                      "ml-auto h-4 w-4",
                      tag?.value === selectedTag ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
